/**
 * Axios Auth Client
 * Handles authentication API calls with Set-Cookie session management
 */

import axios from "axios";
import {
  AUTH_BASE_URL,
  BASE_URL
} from "../Config/Config";

/**
 * Create axios instance for authentication endpoints
 * Handles Set-Cookie extraction and session management
 */
export const authAxiosInstance = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true, // Enable cookie handling
});

/**
 * Create axios instance for API endpoints
 * Uses session cookies for authentication
 */
export const apiAxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Response interceptor for API endpoints
apiAxiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error(
        "API response error:",
        error.response.status,
        error.response.data,
      );
    }
    return Promise.reject(error);
  },
);

/**
 * Login with username and password
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {number} applicationId - Application ID
 * @returns {Promise} Response data
 */
export const loginWithAxios = async (username, password, applicationId) => {
  try {
    const response = await authAxiosInstance.post("/auth/login", {
      username,
      password,
      applicationId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify OTP
 * @param {string} otp - OTP code
 * @param {string} username - Username
 * @param {number} applicationId - Application ID
 * @returns {Promise} Response data
 */
export const verifyOtpWithAxios = async (otp, username, applicationId) => {
  try {
    const response = await authAxiosInstance.post("/auth/verify", {
      otp,
      username,
      applicationId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Resend OTP
 * @param {string} username - Username
 * @param {string} otpby - OTP delivery method (email/sms)
 * @returns {Promise} Response data
 */
export const resendOtpWithAxios = async (username, otpby) => {
  try {
    const response = await authAxiosInstance.post("resendotp", {
      username,
      otpby,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify OTP
 * @param {string} otp - OTP code
 * @param {string} username - Username
 * @param {number} applicationId - Application ID
 * @returns {Promise} Response data
 */
export const changePasswordWithAxios = async (
  currentPassword,
  newPassword,
  confirmPassword,
  applicationId,
  token
) => {
  try {
    const response = await authAxiosInstance.post(
      "/auth/change-password",
      {
        currentPassword,
        newPassword,
        confirmPassword,
        applicationId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Initialize all axios interceptors
 */
export const initializeAxiosClients = () => {};

export default {
  loginWithAxios,
  verifyOtpWithAxios,
  resendOtpWithAxios,
  initializeAxiosClients,
};
